/**
 * Patient Registry — search, recall, upsert.
 *
 * Sprint K-Recall. Backs the Patient Lookup feature (Combined
 * Improvements Addendum §5). Two read paths and one write path:
 *
 *   searchPatients({ query, dob }) → returns top matches by name +
 *     DOB across the calling user's agency.
 *
 *   recallPatient(patientId) → returns the full patient record with
 *     active danger flags pre-joined. Bumps last_seen_at +
 *     encounter_count on read so QA can spot frequent flyers.
 *
 *   upsertPatientFromEpcr(epcr) → match-or-create by (agency_id,
 *     last_name, first_name, dob). Called on ePCR finalize / lock to
 *     accumulate the registry. Returns the patient id so the ePCR
 *     can store a back-reference.
 *
 * All calls go through the authenticated Supabase client; RLS
 * enforces agency scope. Devs running without VITE_SUPABASE_URL get
 * empty results / no-op writes so local development stays unblocked.
 */

import { supabase } from './supabase';
import type { EPCR } from '@/types';

export interface RegistryPatient {
  id: string;
  agency_id: string;
  first_name: string | null;
  middle_name: string | null;
  last_name: string | null;
  suffix: string | null;
  dob: string | null;
  gender: string | null;
  race: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  email: string | null;
  patient_id_number: string | null;
  patient_id_state: string | null;
  patient_id_type: string | null;
  insurance_carrier: string | null;
  insurance_policy: string | null;
  medical_history: string[];
  allergies: unknown[];
  home_medications: unknown[];
  encounter_count: number;
  first_seen_at: string | null;
  last_seen_at: string | null;
  last_epcr_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface PatientDangerFlag {
  id: string;
  patient_id: string;
  agency_id: string;
  category:
    | 'violent_history' | 'weapons_in_home' | 'le_staging_required'
    | 'communicable_disease' | 'psych_history' | 'dv_indicator'
    | 'frequent_flyer' | 'severe_allergy' | 'dnr_polst'
    | 'special_needs' | 'hoarder_unsafe';
  severity: 'low' | 'medium' | 'high' | null;
  status: 'candidate' | 'active' | 'under_review' | 'expired' | 'retired';
  source_epcr_id: string | null;
  notes: string | null;
  added_by: string | null;
  added_at: string;
  reviewed_by: string | null;
  reviewed_at: string | null;
  effective_until: string | null;
}

export interface RecallResult {
  patient: RegistryPatient;
  flags: PatientDangerFlag[];
}

function devMode(): boolean {
  return !import.meta.env.VITE_SUPABASE_URL;
}

/**
 * Search by name fragments + optional DOB. Case-insensitive partial
 * match on last_name (most discriminating) AND on first_name when
 * provided. DOB is exact match when supplied.
 *
 * Returns up to 25 candidates ordered by last_seen_at desc — recent
 * patients surface first.
 */
export async function searchPatients(args: {
  lastName?: string;
  firstName?: string;
  dob?: string;            // YYYY-MM-DD
  phone?: string;
  idNumber?: string;
}): Promise<RegistryPatient[]> {
  if (devMode()) return [];
  let q = supabase
    .from('patients')
    .select('*')
    .order('last_seen_at', { ascending: false, nullsFirst: false })
    .limit(25);

  if (args.lastName?.trim())  q = q.ilike('last_name',  `${args.lastName.trim()}%`);
  if (args.firstName?.trim()) q = q.ilike('first_name', `${args.firstName.trim()}%`);
  if (args.dob)               q = q.eq('dob', args.dob);
  if (args.phone?.trim())     q = q.eq('phone', args.phone.trim());
  if (args.idNumber?.trim())  q = q.eq('patient_id_number', args.idNumber.trim());

  const { data, error } = await q;
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[patient-registry] searchPatients failed:', error.message);
    return [];
  }
  return (data ?? []) as RegistryPatient[];
}

/**
 * Recall a single patient by id with their active danger flags
 * pre-joined. We DON'T bump last_seen_at on read — that happens on
 * upsert when a new ePCR is created from this patient.
 */
export async function recallPatient(patientId: string): Promise<RecallResult | null> {
  if (devMode()) return null;
  const [pat, flags] = await Promise.all([
    supabase.from('patients').select('*').eq('id', patientId).single(),
    supabase.from('patient_danger_flags')
      .select('*')
      .eq('patient_id', patientId)
      .in('status', ['active', 'candidate'])
      .order('added_at', { ascending: false }),
  ]);
  if (pat.error || !pat.data) return null;
  return {
    patient: pat.data as RegistryPatient,
    flags: (flags.data ?? []) as PatientDangerFlag[],
  };
}

/**
 * Project the registry-side patient onto an ePCR-shaped patch the
 * caller can pass to onUpdate(). We deliberately DON'T overwrite
 * patient_first_name etc. when the registry value is null — caller's
 * existing data wins for empty registry fields.
 */
export function patientToEpcrPatch(p: RegistryPatient): Partial<EPCR> {
  const patch: Partial<EPCR> = {};
  if (p.first_name)        patch.patient_first_name = p.first_name;
  if (p.middle_name)       patch.patient_middle_name = p.middle_name;
  if (p.last_name)         patch.patient_last_name = p.last_name;
  if (p.suffix)            patch.patient_suffix = p.suffix;
  if (p.dob)               patch.patient_dob = p.dob;
  if (p.gender)            patch.patient_gender = p.gender;
  if (p.race)              patch.patient_race = p.race;
  if (p.address)           patch.patient_address = p.address;
  if (p.city)              patch.patient_city = p.city;
  if (p.state)             patch.patient_state = p.state;
  if (p.zip)               patch.patient_zip = p.zip;
  if (p.patient_id_number) patch.patient_id_number = p.patient_id_number;
  if (p.patient_id_state)  patch.patient_id_state = p.patient_id_state;
  if (p.patient_id_type)   patch.patient_id_type = p.patient_id_type as EPCR['patient_id_type'];
  if (p.insurance_carrier) patch.insurance_carrier = p.insurance_carrier;
  if (p.insurance_policy)  patch.insurance_policy = p.insurance_policy;
  if (p.medical_history?.length) patch.medical_history = p.medical_history;
  // allergies / current_medications shapes match jsonb roughly — pass
  // through. Caller can refine after recall if structure drifts.
  if (Array.isArray(p.allergies) && p.allergies.length > 0) {
    patch.allergies = p.allergies as EPCR['allergies'];
  }
  if (Array.isArray(p.home_medications) && p.home_medications.length > 0) {
    patch.current_medications = p.home_medications as EPCR['current_medications'];
  }
  return patch;
}

/**
 * Match-or-create on ePCR finalize. Called from the lock / QA
 * approval handler so the registry accumulates exactly the records
 * the EMT signed off on.
 *
 * Returns the patient id (existing or new). Idempotent — repeat
 * calls update the same row.
 */
export async function upsertPatientFromEpcr(epcr: EPCR): Promise<string | null> {
  if (devMode()) return null;
  if (!epcr.patient_last_name && !epcr.patient_first_name) return null;
  const now = new Date().toISOString();

  // Try to find an existing match.
  const { data: existing } = await supabase
    .from('patients')
    .select('id, encounter_count, first_seen_at')
    .eq('agency_id', epcr.agency_id)
    .ilike('last_name', epcr.patient_last_name ?? '')
    .ilike('first_name', epcr.patient_first_name ?? '')
    .eq('dob', epcr.patient_dob ?? null)
    .maybeSingle();

  const payload = {
    agency_id: epcr.agency_id,
    first_name: epcr.patient_first_name,
    middle_name: epcr.patient_middle_name,
    last_name: epcr.patient_last_name,
    suffix: epcr.patient_suffix,
    dob: epcr.patient_dob,
    gender: epcr.patient_gender,
    race: epcr.patient_race,
    address: epcr.patient_address,
    city: epcr.patient_city,
    state: epcr.patient_state,
    zip: epcr.patient_zip,
    patient_id_number: epcr.patient_id_number,
    patient_id_state: epcr.patient_id_state,
    patient_id_type: epcr.patient_id_type,
    insurance_carrier: epcr.insurance_carrier,
    insurance_policy: epcr.insurance_policy,
    medical_history: epcr.medical_history,
    allergies: epcr.allergies,
    home_medications: epcr.current_medications,
    last_seen_at: now,
    last_epcr_id: epcr.id,
  };

  if (existing?.id) {
    await supabase.from('patients').update({
      ...payload,
      encounter_count: (existing.encounter_count ?? 0) + 1,
      first_seen_at: existing.first_seen_at ?? now,
    }).eq('id', existing.id);
    return existing.id;
  }

  const { data, error } = await supabase.from('patients').insert({
    ...payload,
    encounter_count: 1,
    first_seen_at: now,
  }).select('id').single();
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[patient-registry] upsert failed:', error.message);
    return null;
  }
  return data?.id ?? null;
}

/**
 * Pre-arrival address-match: find ANY patient at this address with
 * active or candidate danger flags. Used by the dispatch / incident
 * screen to surface flags BEFORE the crew approaches the door.
 *
 * Returns one banner-ready summary per address — collapses multiple
 * patients at the same address into a single "this address has N
 * flagged patients" signal.
 */
export async function findFlagsAtAddress(args: {
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}): Promise<{ patient: RegistryPatient; flags: PatientDangerFlag[] }[]> {
  if (devMode()) return [];
  const addr = args.address?.trim();
  if (!addr || addr.length < 3) return [];

  // Match on street address (required) + zip when present (cheap
  // narrowing). City + state could match too but addresses are
  // usually distinctive enough on their own.
  let q = supabase
    .from('patients')
    .select('*, flags:patient_danger_flags(*)')
    .ilike('address', `%${addr}%`);
  if (args.zip) q = q.eq('zip', args.zip);
  q = q.limit(20);
  const { data, error } = await q;
  if (error || !data) return [];

  type Joined = RegistryPatient & { flags: PatientDangerFlag[] };
  return (data as Joined[])
    .map((p) => {
      const { flags, ...rest } = p;
      const active = (flags ?? []).filter((f) => f.status === 'active' || f.status === 'candidate');
      return { patient: rest as RegistryPatient, flags: active };
    })
    .filter((r) => r.flags.length > 0);
}

export const DANGER_FLAG_LABEL: Record<PatientDangerFlag['category'], string> = {
  violent_history:      'Violent history',
  weapons_in_home:      'Weapons in home',
  le_staging_required:  'LE staging recommended',
  communicable_disease: 'Communicable disease',
  psych_history:        'Psych history',
  dv_indicator:         'DV indicator',
  frequent_flyer:       'Frequent flyer',
  severe_allergy:       'Severe allergy',
  dnr_polst:            'DNR / POLST',
  special_needs:        'Special needs',
  hoarder_unsafe:       'Hoarder / unsafe',
};

export const DANGER_FLAG_TONE: Record<PatientDangerFlag['category'], 'red' | 'amber' | 'blue' | 'slate'> = {
  violent_history:      'red',
  weapons_in_home:      'red',
  le_staging_required:  'amber',
  communicable_disease: 'amber',
  psych_history:        'amber',
  dv_indicator:         'amber',
  frequent_flyer:       'slate',
  severe_allergy:       'red',
  dnr_polst:            'amber',
  special_needs:        'blue',
  hoarder_unsafe:       'amber',
};
